import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Plus, Trash2, ExternalLink, Download, ChevronLeft, ChevronRight, MapPin, Clock, X, Check, AlertCircle, XCircle, ChevronDown, Lock, Video } from "lucide-react";
import { CATEGORIES as CAT_KEYS, CATEGORY_LABEL, isStaff, canRsvpEvent } from "@/lib/roles";
import AddressAutocomplete from "@/components/AddressAutocomplete";

const MONTHS = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
const WEEKDAYS = ["LUN","MAR","MIÉ","JUE","VIE","SÁB","DOM"];
const TYPE_COLOR = {
  game: "#FF3B30",      // red - kinetic
  practice: "#34C759",  // green - field
  meeting: "#007AFF",   // blue
  other: "#A1A1AA",
};
const TYPE_LABEL = { game: "Partido", practice: "Entrenamiento", meeting: "Reunión", other: "Otro" };

const STAFF_AUDIENCE_LABELS = {
  directiva: "Directiva",
  coach_masculino: "Coach Masculino",
  coach_femenino: "Coach Femenino",
};
const labelForCat = (c) => CATEGORY_LABEL[c] || STAFF_AUDIENCE_LABELS[c] || c;

const fmtTime = (iso) => new Date(iso).toLocaleTimeString("es", { hour: "2-digit", minute: "2-digit" });
const fmtDateTime = (iso) => new Date(iso).toLocaleString("es", { dateStyle: "medium", timeStyle: "short" });
const ymd = (date) => {
  // Use LOCAL date components so the "today" / day cells use the user's
  // wall-clock date (Chile UTC-4) instead of UTC (toISOString → UTC date
  // which flips at 8pm Chile time and made HOY land on the wrong day).
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

export default function CalendarPage() {
  const { user, profile } = useAuth();
  const [events, setEvents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [cursor, setCursor] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const [selectedDay, setSelectedDay] = useState(null);
  const canEdit = isStaff(user?.role);

  const load = () => api.get("/events").then(r => setEvents(r.data));
  useEffect(() => { load(); }, []);

  const updateRsvp = async (eventId, status) => {
    // Optimistic
    setEvents(prev => prev.map(e => {
      if (e.id !== eventId) return e;
      const counts = { ...(e.rsvp_counts || { going:0, late:0, not_going:0 }) };
      if (e.my_rsvp && counts[e.my_rsvp] > 0) counts[e.my_rsvp] -= 1;
      counts[status] = (counts[status] || 0) + 1;
      return { ...e, my_rsvp: status, rsvp_counts: counts };
    }));
    try {
      await api.post(`/events/${eventId}/rsvp`, { status });
      toast.success(status === "going" ? "Confirmado · ¡Te esperamos!" : status === "late" ? "Llegada tarde marcada" : "Marcaste que no puedes asistir");
    } catch {
      toast.error("Error al guardar");
      load();
    }
  };

  const onDelete = async (id) => {
    if (!confirm("¿Eliminar evento?")) return;
    await api.delete(`/events/${id}`);
    toast.success("Evento eliminado");
    load();
  };

  const downloadICS = async (eventId) => {
    const url = eventId ? `/events/${eventId}/ics` : "/events/calendar.ics";
    const res = await api.get(url, { responseType: "blob" });
    const blob = new Blob([res.data], { type: "text/calendar" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = eventId ? `event-${eventId}.ics` : "ferroviarios.ics";
    a.click();
  };

  const openInGoogle = (ev) => {
    const dtStart = new Date(ev.starts_at).toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
    const dtEnd = new Date(ev.ends_at).toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
    const params = new URLSearchParams({
      action: "TEMPLATE", text: ev.title, dates: `${dtStart}/${dtEnd}`,
      details: ev.description || "", location: ev.location || "",
    });
    window.open(`https://calendar.google.com/calendar/render?${params.toString()}`, "_blank");
  };

  // Group events by YYYY-MM-DD
  const eventsByDate = useMemo(() => {
    const map = {};
    events.forEach(ev => {
      const key = ymd(new Date(ev.starts_at));
      (map[key] ||= []).push(ev);
    });
    Object.values(map).forEach(arr => arr.sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at)));
    return map;
  }, [events]);

  // Visible month days
  const monthGrid = useMemo(() => {
    const year = cursor.getFullYear();
    const month = cursor.getMonth();
    const first = new Date(year, month, 1);
    const last = new Date(year, month + 1, 0);
    // weekday: lunes=0
    const startOffset = (first.getDay() + 6) % 7;
    const totalDays = last.getDate();
    const cells = [];
    // prev month tail
    for (let i = startOffset - 1; i >= 0; i--) {
      const d = new Date(year, month, -i);
      cells.push({ date: d, outside: true });
    }
    for (let d = 1; d <= totalDays; d++) {
      cells.push({ date: new Date(year, month, d), outside: false });
    }
    // pad to multiple of 7
    while (cells.length % 7 !== 0) {
      const last = cells[cells.length - 1].date;
      const nd = new Date(last); nd.setDate(last.getDate() + 1);
      cells.push({ date: nd, outside: true });
    }
    return cells;
  }, [cursor]);

  const prevMonth = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1));
  const nextMonth = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1));
  const today = new Date();
  const todayKey = ymd(today);

  // Listed events (filtered if a day is selected, else show month + future)
  const listEvents = useMemo(() => {
    if (selectedDay) {
      const key = ymd(selectedDay);
      return eventsByDate[key] || [];
    }
    const year = cursor.getFullYear(), month = cursor.getMonth();
    return events
      .filter(ev => {
        const d = new Date(ev.starts_at);
        return d.getFullYear() === year && d.getMonth() === month;
      })
      .sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at));
  }, [events, eventsByDate, cursor, selectedDay]);

  return (
    <Layout>
      <div className="px-6 md:px-10 py-8 max-w-[1600px]">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="overline">Game Plan</div>
            <h1 className="font-display text-4xl sm:text-5xl text-white leading-none">CALENDARIO</h1>
            <p className="text-[#A1A1AA] mt-2 text-sm">Partidos, entrenamientos y reuniones del club.</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => downloadICS(null)} className="btn-ghost text-xs flex items-center gap-2" data-testid="download-all-ics">
              <Download size={14}/> .ics
            </button>
            {canEdit && (
              <button onClick={() => setShowForm(true)} className="btn-primary text-xs flex items-center gap-2" data-testid="new-event-btn">
                <Plus size={14}/> Nuevo evento
              </button>
            )}
          </div>
        </div>

        {showForm && <EventForm onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }}/>}

        {/* Month navigator + Calendar Grid */}
        <div className="tactical-card mb-6 overflow-hidden">
          {/* Field-style header */}
          <div className="relative px-6 py-5 border-b border-[#27272A]" style={{
            background: "linear-gradient(90deg, rgba(13,40,24,0.8) 0%, rgba(10,10,10,0.95) 60%, rgba(13,40,24,0.8) 100%)"
          }}>
            <div className="absolute inset-0 opacity-30" style={{
              backgroundImage: "repeating-linear-gradient(90deg, transparent, transparent 9.9%, rgba(255,255,255,0.1) 10%)"
            }}/>
            <div className="relative flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <button onClick={prevMonth} className="btn-ghost text-xs p-2" data-testid="cal-prev"><ChevronLeft size={16}/></button>
                <div>
                  <div className="overline text-[#34C759]">Mes</div>
                  <div className="font-display text-3xl text-white leading-none">
                    {MONTHS[cursor.getMonth()]} <span className="text-[#22d3ee]">{cursor.getFullYear()}</span>
                  </div>
                </div>
                <button onClick={nextMonth} className="btn-ghost text-xs p-2" data-testid="cal-next"><ChevronRight size={16}/></button>
              </div>
              <div className="flex items-center gap-3 flex-wrap text-[10px]">
                <button onClick={() => { setCursor(new Date(today.getFullYear(), today.getMonth(), 1)); setSelectedDay(today); }} className="btn-ghost text-xs">HOY</button>
                <Legend color={TYPE_COLOR.game} label="Partido"/>
                <Legend color={TYPE_COLOR.practice} label="Entrenamiento"/>
                <Legend color={TYPE_COLOR.meeting} label="Reunión"/>
              </div>
            </div>
          </div>

          {/* Weekday header */}
          <div className="grid grid-cols-7 border-b border-[#27272A]">
            {WEEKDAYS.map(w => (
              <div key={w} className="px-2 py-2 text-center overline border-r border-[#27272A] last:border-r-0">{w}</div>
            ))}
          </div>

          {/* Days grid */}
          <div className="grid grid-cols-7">
            {monthGrid.map((cell, idx) => {
              const key = ymd(cell.date);
              const dayEvents = eventsByDate[key] || [];
              const isToday = key === todayKey;
              const isSelected = selectedDay && ymd(selectedDay) === key;
              return (
                <button
                  key={idx}
                  onClick={() => setSelectedDay(cell.date)}
                  data-testid={`day-${key}`}
                  className={`relative aspect-square md:aspect-auto md:min-h-[100px] p-2 text-left border-r border-b border-[#27272A] transition-colors ${
                    cell.outside ? "bg-[#0A0A0A] text-[#3f3f46]" : "bg-[#121212] text-white hover:bg-[#1A1A1A]"
                  } ${isSelected ? "ring-2 ring-inset ring-[#007AFF]" : ""}`}
                  style={(idx % 7 === 6) ? { borderRightWidth: 0 } : {}}
                >
                  <div className={`flex items-center justify-between mb-1`}>
                    <span className={`font-display text-lg ${isToday ? "text-[#FF3B30]" : ""} ${cell.outside ? "opacity-40" : ""}`}>
                      {cell.date.getDate()}
                    </span>
                    {isToday && <span className="text-[8px] uppercase tracking-widest text-[#FF3B30] font-bold">HOY</span>}
                  </div>
                  <div className="space-y-1">
                    {dayEvents.slice(0, 3).map(ev => (
                      <div
                        key={ev.id}
                        className="text-[10px] truncate border-l-2 pl-1.5 py-0.5"
                        style={{ borderColor: TYPE_COLOR[ev.event_type], color: cell.outside ? "#52525b" : "#fff" }}
                        title={ev.title}
                      >
                        <span className="font-bold">{fmtTime(ev.starts_at)}</span> {ev.title}
                      </div>
                    ))}
                    {dayEvents.length > 3 && (
                      <div className="text-[9px] text-[#71717A] uppercase tracking-wider">+{dayEvents.length - 3} más</div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Selected day banner */}
        {selectedDay && (
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="overline">Mostrando</div>
              <div className="font-display text-xl text-white">
                {selectedDay.toLocaleDateString("es", { weekday: "long", day: "numeric", month: "long" }).toUpperCase()}
              </div>
            </div>
            <button onClick={() => setSelectedDay(null)} className="btn-ghost text-xs flex items-center gap-1">
              <X size={12}/> Mostrar todo el mes
            </button>
          </div>
        )}

        {/* Event listing */}
        <div className="space-y-3">
          {listEvents.length === 0 && (
            <div className="tactical-card p-10 text-center">
              <div className="text-[#A1A1AA]">{selectedDay ? "Sin eventos en este día." : "Sin eventos este mes."}</div>
            </div>
          )}
          {listEvents.map(ev => (
            <EventCard
              key={ev.id}
              ev={ev}
              user={user}
              profile={profile}
              canEdit={canEdit}
              onRsvp={updateRsvp}
              onDelete={onDelete}
              onAddGoogle={openInGoogle}
              onDownloadIcs={downloadICS}
            />
          ))}
        </div>
        <div className="h-20 md:h-6"/>
      </div>
    </Layout>
  );
}

const Legend = ({ color, label }) => (
  <span className="flex items-center gap-1.5 text-[#A1A1AA] uppercase tracking-wider">
    <span className="w-2.5 h-2.5" style={{ background: color }}/> {label}
  </span>
);

function EventForm({ onClose, onSaved }) {
  const { user } = useAuth();
  const isAdmin = !isStaff(user?.role) ? false : (user?.role && !["coach"].includes(user.role));
  const coachCats = user?.coach_categories || [];
  const availableCats = isAdmin ? CAT_KEYS : (coachCats.length ? coachCats : CAT_KEYS);
  // Audiencias staff disponibles según el rol:
  // - Admin: las 3 (Directiva, Coach M., Coach F.)
  // - Coach: solo las relevantes a sus categorías (sin "Directiva")
  const availableAudiences = useMemo(() => {
    const out = [];
    if (isAdmin) {
      out.push({ key: "directiva",       label: "Directiva" });
      out.push({ key: "coach_masculino", label: "Coach Masculino" });
      out.push({ key: "coach_femenino",  label: "Coach Femenino" });
    } else if (user?.role === "coach") {
      const set = new Set(coachCats);
      if (set.has("tackle") || set.has("flag_masculino")) {
        out.push({ key: "coach_masculino", label: "Coach Masculino" });
      }
      if (set.has("flag_femenino")) {
        out.push({ key: "coach_femenino", label: "Coach Femenino" });
      }
    }
    return out;
  }, [isAdmin, user?.role, coachCats]);
  const [mode, setMode] = useState("single"); // single | bulk
  const [data, setData] = useState({
    title: "", description: "", event_type: "practice",
    starts_at: "", ends_at: "",
    location: "", meeting_url: "", opponent: "",
    categories: [], // empty = todo el club (general)
  });
  // Bulk
  const [bulkDate, setBulkDate] = useState("");
  const [bulkTime, setBulkTime] = useState("19:00");
  const [bulkDuration, setBulkDuration] = useState(120);
  const [bulkDates, setBulkDates] = useState([]); // [{date, time}]
  // Repeat helper
  const [weekday, setWeekday] = useState("2"); // tue
  const [weeksAhead, setWeeksAhead] = useState(4);
  const [saving, setSaving] = useState(false);

  const addBulkDate = () => {
    if (!bulkDate || !bulkTime) return;
    const key = `${bulkDate}T${bulkTime}`;
    if (bulkDates.find(d => `${d.date}T${d.time}` === key)) return;
    setBulkDates([...bulkDates, { date: bulkDate, time: bulkTime }].sort((a, b) => `${a.date}T${a.time}`.localeCompare(`${b.date}T${b.time}`)));
    setBulkDate("");
  };

  const removeBulkDate = (idx) => setBulkDates(bulkDates.filter((_, i) => i !== idx));

  const addWeeklyRecurrence = () => {
    // Add next N occurrences of `weekday` starting today
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const targetDow = parseInt(weekday); // 0=domingo .. 6=sábado
    const out = [...bulkDates];
    let d = new Date(today);
    // find first matching day from today
    while (d.getDay() !== targetDow) d.setDate(d.getDate() + 1);
    for (let i = 0; i < parseInt(weeksAhead); i++) {
      const date = ymd(d);
      if (!out.find(x => x.date === date && x.time === bulkTime)) {
        out.push({ date, time: bulkTime });
      }
      d.setDate(d.getDate() + 7);
    }
    out.sort((a, b) => `${a.date}T${a.time}`.localeCompare(`${b.date}T${b.time}`));
    setBulkDates(out);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (mode === "single") {
        const payload = {
          ...data,
          starts_at: new Date(data.starts_at).toISOString(),
          ends_at: new Date(data.ends_at).toISOString(),
        };
        await api.post("/events", payload);
        toast.success("Evento creado");
      } else {
        if (bulkDates.length === 0) { toast.error("Agrega al menos una fecha"); setSaving(false); return; }
        const payload = {
          title: data.title,
          description: data.description,
          event_type: data.event_type,
          location: data.location,
          meeting_url: data.meeting_url,
          opponent: data.opponent,
          categories: data.categories,
          duration_minutes: parseInt(bulkDuration),
          dates: bulkDates.map(d => new Date(`${d.date}T${d.time}:00`).toISOString()),
        };
        const r = await api.post("/events/bulk", payload);
        toast.success(`${r.data.length} eventos creados`);
      }
      onSaved();
    } catch (err) {
      toast.error("Error al crear");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="tactical-card w-full max-w-3xl my-8 flex flex-col max-h-[90vh]"
        data-testid="event-form"
      >
        {/* Sticky header: title + close + mode toggle so user can always switch back to Una fecha */}
        <div className="sticky top-0 z-10 bg-[#0A0A0A] border-b border-[#27272A] p-6 pb-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="overline">Game Plan</div>
              <div className="font-display text-2xl text-white">NUEVO EVENTO</div>
            </div>
            <button type="button" onClick={onClose} className="btn-ghost text-xs">✕</button>
          </div>
          <div className="flex border border-[#27272A]">
            <button type="button" onClick={() => setMode("single")} data-testid="mode-single"
              className={`flex-1 py-3 text-xs uppercase tracking-wider font-bold ${mode==="single" ? "bg-[#1A1A1A] text-white border-b-2 border-b-[#007AFF]" : "text-[#A1A1AA] hover:text-white"}`}>
              Una fecha
            </button>
            <button type="button" onClick={() => setMode("bulk")} data-testid="mode-bulk"
              className={`flex-1 py-3 text-xs uppercase tracking-wider font-bold border-l border-[#27272A] ${mode==="bulk" ? "bg-[#1A1A1A] text-white border-b-2 border-b-[#007AFF]" : "text-[#A1A1AA] hover:text-white"}`}>
              Múltiples fechas
            </button>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto p-6 pt-5 flex-1">
        {/* Common fields */}
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="label-tactical">Título *</label>
            <input required className="input-tactical" value={data.title} onChange={e => setData({...data, title: e.target.value})} data-testid="event-title-input"/>
          </div>
          <div>
            <label className="label-tactical">Tipo</label>
            <select className="input-tactical" value={data.event_type} onChange={e => setData({...data, event_type: e.target.value})} data-testid="event-type-select">
              <option value="practice">Entrenamiento</option>
              <option value="game">Partido</option>
              <option value="meeting">Reunión</option>
              <option value="other">Otro</option>
            </select>
          </div>
          <div>
            <label className="label-tactical">Ubicación</label>
            <AddressAutocomplete
              value={data.location}
              onChange={(v) => setData({ ...data, location: v })}
              testid="event-location-input"
            />
          </div>
          <div>
            <label className="label-tactical">Oponente (si aplica)</label>
            <input className="input-tactical" value={data.opponent} onChange={e => setData({...data, opponent: e.target.value})}/>
          </div>
          {data.event_type === "meeting" && (
            <div className="md:col-span-2" data-testid="meeting-url-field">
              <label className="label-tactical flex items-center gap-1.5">
                <Video size={11} className="text-[#A78BFA]"/> Link de reunión (Meet/Zoom)
              </label>
              <input
                className="input-tactical"
                placeholder="https://meet.google.com/... o https://zoom.us/j/..."
                value={data.meeting_url}
                onChange={e => setData({...data, meeting_url: e.target.value})}
                data-testid="event-meeting-input"
              />
              <div className="text-[10px] text-[#71717A] mt-1.5 leading-relaxed">
                <strong className="text-cyan-300/90">Auto-Meet:</strong> si dejas este campo vacío y tu Google Calendar está conectado en tu perfil,
                el sistema generará automáticamente un link de Google Meet único al crear la reunión.
                También podés pegar manualmente un link de Zoom u otra plataforma.
              </div>
            </div>
          )}
          <div className="md:col-span-2">
            <label className="label-tactical">Descripción</label>
            <textarea rows={2} className="input-tactical" value={data.description} onChange={e => setData({...data, description: e.target.value})}/>
          </div>
          <div className="md:col-span-2">
            <label className="label-tactical">Categorías del evento</label>
            <div className="flex flex-wrap gap-2">
              {availableCats.map(c => {
                const active = data.categories.includes(c);
                return (
                  <button
                    type="button"
                    key={c}
                    onClick={() => setData(d => ({
                      ...d,
                      categories: d.categories.includes(c) ? d.categories.filter(x => x !== c) : [...d.categories, c],
                    }))}
                    data-testid={`ev-cat-${c}`}
                    className={`px-3 py-2 border text-xs uppercase tracking-wider font-bold transition-colors ${active ? "border-[#007AFF] bg-[#1A1A1A] text-white" : "border-[#27272A] text-[#A1A1AA] hover:text-white"}`}
                  >
                    {CATEGORY_LABEL[c]}
                  </button>
                );
              })}
            </div>
            {/* Audiencias staff: solo para reuniones */}
            {data.event_type === "meeting" && availableAudiences.length > 0 && (
              <div className="mt-3" data-testid="ev-staff-audiences">
                <div className="overline text-[#71717A] mb-1.5 text-[10px]">
                  Audiencia staff (reuniones)
                </div>
                <div className="flex flex-wrap gap-2">
                  {availableAudiences.map(a => {
                    const active = data.categories.includes(a.key);
                    return (
                      <button
                        type="button"
                        key={a.key}
                        onClick={() => setData(d => ({
                          ...d,
                          categories: d.categories.includes(a.key) ? d.categories.filter(x => x !== a.key) : [...d.categories, a.key],
                        }))}
                        data-testid={`ev-aud-${a.key}`}
                        className={`px-3 py-2 border text-xs uppercase tracking-wider font-bold transition-colors ${active ? "border-purple-400 bg-[#1A1A1A] text-white" : "border-[#27272A] text-[#A1A1AA] hover:text-white"}`}
                      >
                        {a.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="text-[10px] text-[#71717A] mt-1.5">
              {data.categories.length === 0
                ? "Sin selección → evento visible para todo el club (solo Super Admin / Directivo puede asignar al club entero)."
                : "Solo los miembros / audiencias seleccionadas serán notificadas y podrán confirmar asistencia."}
            </div>
          </div>
        </div>

        {mode === "single" ? (
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="label-tactical">Inicio *</label>
              <input required type="datetime-local" className="input-tactical" value={data.starts_at} onChange={e => setData({...data, starts_at: e.target.value})} data-testid="event-start-input"/>
            </div>
            <div>
              <label className="label-tactical">Fin *</label>
              <input required type="datetime-local" className="input-tactical" value={data.ends_at} onChange={e => setData({...data, ends_at: e.target.value})} data-testid="event-end-input"/>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="label-tactical">Fecha</label>
                <input type="date" className="input-tactical" value={bulkDate} onChange={e => setBulkDate(e.target.value)} data-testid="bulk-date-input"/>
              </div>
              <div>
                <label className="label-tactical">Hora</label>
                <input type="time" className="input-tactical" value={bulkTime} onChange={e => setBulkTime(e.target.value)} data-testid="bulk-time-input"/>
              </div>
              <div>
                <label className="label-tactical">Duración (min)</label>
                <input type="number" min={15} step={15} className="input-tactical" value={bulkDuration} onChange={e => setBulkDuration(e.target.value)}/>
              </div>
            </div>
            <button type="button" onClick={addBulkDate} className="btn-ghost text-xs flex items-center gap-2" data-testid="add-bulk-date">
              <Plus size={12}/> Agregar fecha
            </button>

            <div className="border-t border-[#27272A] pt-4">
              <div className="overline mb-3">Recurrencia rápida (semanal)</div>
              <div className="grid md:grid-cols-3 gap-3 items-end">
                <div>
                  <label className="label-tactical">Día de la semana</label>
                  <select className="input-tactical" value={weekday} onChange={e => setWeekday(e.target.value)}>
                    <option value="1">Lunes</option>
                    <option value="2">Martes</option>
                    <option value="3">Miércoles</option>
                    <option value="4">Jueves</option>
                    <option value="5">Viernes</option>
                    <option value="6">Sábado</option>
                    <option value="0">Domingo</option>
                  </select>
                </div>
                <div>
                  <label className="label-tactical">N° de semanas</label>
                  <input type="number" min={1} max={26} className="input-tactical" value={weeksAhead} onChange={e => setWeeksAhead(e.target.value)}/>
                </div>
                <button type="button" onClick={addWeeklyRecurrence} className="btn-primary text-xs" data-testid="add-weekly">
                  AGREGAR RECURRENTE
                </button>
              </div>
              <div className="text-[10px] text-[#71717A] mt-2">Ej: cada Martes durante 4 semanas a las {bulkTime}</div>
            </div>

            {/* Dates list */}
            <div>
              <div className="overline mb-2">Fechas a crear ({bulkDates.length})</div>
              <div className="border border-[#27272A] max-h-48 overflow-y-auto" data-testid="bulk-dates-list">
                {bulkDates.length === 0 && <div className="p-4 text-[#71717A] text-xs text-center">Agrega fechas arriba</div>}
                {bulkDates.map((d, idx) => (
                  <div key={`${d.date}T${d.time}-${idx}`} className="flex items-center justify-between px-3 py-2 border-b border-[#27272A] last:border-b-0">
                    <div className="text-sm text-white">
                      <span className="font-mono">{d.date}</span>
                      <span className="text-[#71717A] mx-2">·</span>
                      <span>{d.time}</span>
                    </div>
                    <button type="button" onClick={() => removeBulkDate(idx)} className="text-[#71717A] hover:text-[#FF3B30]" data-testid={`rm-bulk-${idx}`}>
                      <X size={14}/>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="flex gap-2 justify-end mt-6 pt-5 border-t border-[#27272A]">
          <button type="button" onClick={onClose} className="btn-ghost text-xs">CANCELAR</button>
          <button type="submit" disabled={saving} className="btn-primary text-xs" data-testid="event-submit-btn">
            {saving ? "GUARDANDO..." : (mode === "bulk" ? `CREAR ${bulkDates.length || ""} EVENTOS` : "CREAR EVENTO")}
          </button>
        </div>
        </div>{/* end scrollable body */}
      </form>
    </div>
  );
}


const buildMapsUrl = (loc) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(loc)}`;
const buildWazeUrl = (loc) =>
  `https://www.waze.com/ul?q=${encodeURIComponent(loc)}&navigate=yes`;

const RSVP_OPTIONS = [
  { key: "going",     label: "VOY",     icon: Check,       color: "#34C759" },
  { key: "late",      label: "TARDE",   icon: AlertCircle, color: "#FF9F0A" },
  { key: "not_going", label: "NO VOY",  icon: XCircle,     color: "#FF3B30" },
];
const RSVP_LABEL = {
  going: "Confirmados",
  late: "Llegan tarde",
  not_going: "Ausentes",
};

function EventCard({ ev, user, profile, canEdit, onRsvp, onDelete, onAddGoogle, onDownloadIcs }) {
  const [attendanceOpen, setAttendanceOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("going");
  const [rsvps, setRsvps] = useState(null);
  const counts = ev.rsvp_counts || { going: 0, late: 0, not_going: 0 };
  const evCats = ev.categories || [];
  const isGeneral = evCats.length === 0;
  const canParticipate = canRsvpEvent(user, profile, ev);

  const loadRsvps = async () => {
    const r = await api.get(`/events/${ev.id}/rsvps`);
    setRsvps(r.data);
  };

  const toggleAttendance = async (tab) => {
    if (attendanceOpen && activeTab === tab) {
      setAttendanceOpen(false);
      return;
    }
    setActiveTab(tab);
    setAttendanceOpen(true);
    if (!rsvps) await loadRsvps();
  };

  return (
    <div
      className="tactical-card p-5"
      data-testid={`cal-event-${ev.id}`}
      style={{ borderLeftWidth: 3, borderLeftColor: TYPE_COLOR[ev.event_type] }}
    >
      <div className="flex items-start gap-4 flex-wrap">
        {/* Date block */}
        <div className="bg-[#0A0A0A] border border-[#27272A] p-3 text-center min-w-[72px]">
          <div className="overline text-[#71717A]">{new Date(ev.starts_at).toLocaleString("es", { month: "short" })}</div>
          <div className="font-display text-3xl text-white leading-none mt-1">{new Date(ev.starts_at).getDate()}</div>
          <div className="text-[10px] text-[#A1A1AA] mt-1 font-mono">{fmtTime(ev.starts_at)}</div>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 border"
              style={{ borderColor: TYPE_COLOR[ev.event_type], color: TYPE_COLOR[ev.event_type] }}
            >
              {TYPE_LABEL[ev.event_type]}
            </span>
            {isGeneral ? (
              <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 border border-[#A78BFA] text-[#A78BFA]" data-testid={`cat-general-${ev.id}`}>
                Todo el club
              </span>
            ) : (
              evCats.map(c => (
                <span key={c} className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 border border-[#27272A] text-[#A1A1AA]" data-testid={`cat-${c}-${ev.id}`}>
                  {labelForCat(c)}
                </span>
              ))
            )}
            <span className="text-[10px] text-[#71717A]">
              <Clock size={10} className="inline mr-1"/>{fmtTime(ev.starts_at)} → {fmtTime(ev.ends_at)}
            </span>
          </div>
          <div className="font-display text-2xl text-white mt-1">{ev.title.toUpperCase()}</div>
          {ev.description && <div className="text-sm text-[#A1A1AA] mt-1">{ev.description}</div>}
          <div className="text-xs text-[#71717A] mt-2 flex flex-wrap gap-3 items-center">
            {ev.location && (
              <span className="flex items-center gap-2">
                <MapPin size={10}/>
                <a
                  href={buildMapsUrl(ev.location)}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[#22d3ee] hover:text-white underline-offset-2 hover:underline"
                  data-testid={`map-link-${ev.id}`}
                >
                  {ev.location}
                </a>
                <a href={buildWazeUrl(ev.location)} target="_blank" rel="noreferrer" className="text-[10px] text-[#71717A] hover:text-white uppercase tracking-wider">Waze</a>
              </span>
            )}
            {ev.opponent && <span>vs {ev.opponent}</span>}
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {ev.meeting_url && (
            <a href={ev.meeting_url} target="_blank" rel="noreferrer" className="btn-ghost text-xs flex items-center gap-1">
              <ExternalLink size={12}/> Reunión
            </a>
          )}
          <button onClick={() => onAddGoogle(ev)} className="btn-ghost text-xs">+ Google Cal</button>
          <button onClick={() => onDownloadIcs(ev.id)} className="btn-ghost text-xs">.ics</button>
          {canEdit && (
            <button onClick={() => onDelete(ev.id)} className="btn-ghost text-xs text-[#FF3B30]" data-testid={`del-event-${ev.id}`}>
              <Trash2 size={12}/>
            </button>
          )}
        </div>
      </div>

      {/* RSVP buttons — solo visibles si el usuario pertenece a la categoría del evento */}
      {canParticipate ? (
        <div className="mt-4 pt-4 border-t border-[#27272A] flex items-center gap-2 flex-wrap" data-testid={`rsvp-block-${ev.id}`}>
          <span className="overline text-[#71717A]">Mi asistencia</span>
          <div className="flex gap-2 flex-wrap">
            {RSVP_OPTIONS.map(opt => {
              const active = ev.my_rsvp === opt.key;
              const Icon = opt.icon;
              return (
                <button
                  key={opt.key}
                  onClick={() => onRsvp(ev.id, opt.key)}
                  data-testid={`rsvp-${opt.key}-${ev.id}`}
                  className={`px-3 py-1.5 border text-[10px] uppercase tracking-widest font-bold flex items-center gap-1.5 transition-colors ${active ? "text-white" : "text-[#A1A1AA] border-[#27272A] hover:text-white"}`}
                  style={active ? { borderColor: opt.color, background: `${opt.color}1A`, color: opt.color } : {}}
                >
                  <Icon size={11}/> {opt.label}
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="mt-4 pt-4 border-t border-[#27272A] flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#52525b]" data-testid={`rsvp-locked-${ev.id}`}>
          <Lock size={10}/> Solo informativo · No perteneces a esta categoría
        </div>
      )}

      {/* Coach/Admin attendance panel */}
      {canEdit && (
        <div className="mt-3 pt-3 border-t border-[#27272A]">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="overline text-[#71717A]">Asistencia</span>
            {RSVP_OPTIONS.map(opt => {
              const isOpen = attendanceOpen && activeTab === opt.key;
              return (
                <button
                  key={opt.key}
                  onClick={() => toggleAttendance(opt.key)}
                  className="flex items-center gap-1.5 border border-[#27272A] hover:border-white px-2.5 py-1 transition-colors"
                  data-testid={`att-toggle-${opt.key}-${ev.id}`}
                  style={isOpen ? { borderColor: opt.color } : {}}
                >
                  <span className="text-[10px] uppercase tracking-wider" style={{ color: opt.color }}>{RSVP_LABEL[opt.key]}</span>
                  <span className="font-display text-base text-white">{counts[opt.key] || 0}</span>
                  <ChevronDown size={10} className={`text-[#71717A] transition-transform ${isOpen ? "rotate-180" : ""}`}/>
                </button>
              );
            })}
          </div>
          {attendanceOpen && (
            <div className="mt-3 border border-[#27272A] bg-[#0A0A0A]" data-testid={`att-list-${ev.id}`}>
              {rsvps === null ? (
                <div className="p-3 text-[#71717A] text-xs">Cargando...</div>
              ) : (
                (() => {
                  const filtered = rsvps.filter(r => r.status === activeTab);
                  if (filtered.length === 0) return <div className="p-3 text-[#71717A] text-xs italic">Nadie en esta categoría aún.</div>;
                  return (
                    <div className="divide-y divide-[#27272A]">
                      {filtered.map(r => (
                        <div key={r.user_id} className="flex items-center gap-3 px-3 py-2">
                          {r.picture ? (
                            <img src={r.picture} alt="" className="w-7 h-7 object-cover"/>
                          ) : (
                            <div className="w-7 h-7 bg-[#1A1A1A] border border-[#27272A] flex items-center justify-center text-white text-[10px] font-bold">{r.name?.[0]}</div>
                          )}
                          <span className="text-sm text-white truncate flex-1">{r.name}</span>
                          <span className="text-[9px] text-[#71717A] uppercase tracking-wider">{r.role}</span>
                        </div>
                      ))}
                    </div>
                  );
                })()
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
